import { cn } from '../../lib/utils';
import type { Severity } from '../../data/types';

interface SeverityBadgeProps {
  severity: Severity;
  showPulse?: boolean;
  className?: string;
}

const severityLabels: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Info',
};

const severityStyles: Record<Severity, string> = {
  critical: 'bg-accent-red/15 text-accent-red border-accent-red/25',
  high: 'bg-accent-orange/15 text-accent-orange border-accent-orange/25',
  medium: 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/25',
  low: 'bg-accent-blue/15 text-accent-blue border-accent-blue/25',
  informational: 'bg-accent-green/15 text-accent-green border-accent-green/25',
};

const pulseColors: Record<Severity, string> = {
  critical: 'bg-accent-red',
  high: 'bg-accent-orange',
  medium: 'bg-accent-yellow',
  low: 'bg-accent-blue',
  informational: 'bg-accent-green',
};

export function SeverityBadge({ severity, showPulse = true, className }: SeverityBadgeProps) {
  const shouldPulse = showPulse && severity === 'critical';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wide border',
        severityStyles[severity],
        className
      )}
    >
      {shouldPulse ? (
        <span className="relative flex h-2 w-2">
          <span
            className={cn(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
              pulseColors[severity]
            )}
          />
          <span
            className={cn('relative inline-flex rounded-full h-2 w-2', pulseColors[severity])}
          />
        </span>
      ) : (
        <span className={cn('inline-flex rounded-full h-2 w-2', pulseColors[severity])} />
      )}
      {severityLabels[severity]}
    </span>
  );
}

